export type BankLogo = {
  src: string
  sourcePage: string
  complete?: boolean
  fit?: 'contain' | 'padded'
}

export type BankClient = {
  name: string
  shortName: string
  tone: string
  logo?: BankLogo
}

export type ImageAsset = {
  src: string
  mobileSrc?: string
  alt: string
  sourceId: string
}

export const navItems = [
  { label: '首页', href: '#home' },
  { label: '解决方案', href: '#solutions' },
  { label: '项目案例', href: '#projects' },
  { label: '制造实力', href: '#manufacturing' },
  { label: '项目服务', href: '#process' },
  { label: '关于昊野', href: '#about' },
  { label: '资讯洞察', href: '#insights' },
  { label: '联系我们', href: '#contact' },
]

const localLogo = (file: string, sourcePage: string, options: Pick<BankLogo, 'complete' | 'fit'> = {}): BankLogo => ({
  src: `/assets/bank-logos/${file}`,
  sourcePage,
  complete: true,
  fit: 'contain',
  ...options,
})

export const bankClients: BankClient[] = [
  { name: '中国银行', shortName: '中国银行', tone: 'red', logo: localLogo('bank-of-china.svg', 'https://cdnlogo.com/logo/bank-of-china_32787.html', { fit: 'padded' }) },
  { name: '中国农业银行', shortName: '农业银行', tone: 'green', logo: localLogo('agricultural-bank-of-china.png', 'https://cdnlogo.com/logo/agricultural-bank-of-china_176815.html', { fit: 'padded' }) },
  { name: '中国工商银行', shortName: '工商银行', tone: 'red', logo: localLogo('icbc.svg', 'https://cdnlogo.com/logo/industrial-and-commercial-bank-of-china_48132.html') },
  { name: '中国邮政储蓄银行', shortName: '邮储银行', tone: 'green', logo: localLogo('postal-savings-bank.svg', 'https://companieslogo.com/postal-savings-bank-of-china/logo/') },
  { name: '交通银行', shortName: '交通银行', tone: 'blue', logo: localLogo('bank-of-communications.svg', 'https://commons.wikimedia.org/wiki/File:Bank_of_Communications_Logo.svg') },
  { name: '中国建设银行', shortName: '建设银行', tone: 'blue', logo: localLogo('ccb.svg', 'https://cdnlogo.com/logo/china-construction-bank_47164.html') },
  { name: '广州农商银行', shortName: '广州农商银行', tone: 'blue', logo: localLogo('guangzhou-rural-commercial-bank.svg', 'https://zh.wikipedia.org/wiki/File:Guangzhou-Rural-Commercial-Bank-Logo.svg') },
  { name: '江门农商银行', shortName: '江门农商银行', tone: 'blue', logo: localLogo('jiangmen-rural-commercial-bank.png', 'https://www.jmrcb.net/jiangmen/') },
  { name: '顺德农商银行', shortName: '顺德农商银行', tone: 'blue', logo: localLogo('shunde-rural-commercial-bank.png', 'https://www.sdebank.com/mainsite/gywm/index.html') },
  { name: '佛山农商银行', shortName: '佛山农商银行', tone: 'orange', logo: localLogo('foshan-rural-commercial-bank.png', 'https://www.foshanbank.cn/foshan/') },
  { name: '平安银行', shortName: '平安银行', tone: 'orange', logo: localLogo('ping-an-bank.svg', 'https://cdnlogo.com/logo/ping-an-bank_37361.html', { fit: 'padded' }) },
  { name: '中信银行', shortName: '中信银行', tone: 'red', logo: localLogo('citic-bank.svg', 'https://cdnlogo.com/logo/china-citic-bank_49020.html') },
  { name: '广州银行', shortName: '广州银行', tone: 'blue', logo: localLogo('guangzhou-bank-wordmark.png', 'https://www.veryicon.com/icons/business/bank-logo-collection/bank-of-guangzhou-portfolio.html', { fit: 'padded' }) },
  { name: '招商银行', shortName: '招商银行', tone: 'red', logo: localLogo('china-merchants-bank.png', 'https://www.cmbchina.com/', { fit: 'padded' }) },
  { name: '中国民生银行', shortName: '民生银行', tone: 'blue', logo: localLogo('china-minsheng-bank.svg', 'https://commons.wikimedia.org/wiki/File:China_Minsheng_Bank_logo.svg') },
  { name: '广发银行', shortName: '广发银行', tone: 'red', logo: localLogo('china-guangfa-bank.svg', 'https://cdnlogo.com/logo/china-guangfa-bank_198600.html') },
  { name: '中建三局', shortName: '中建三局', tone: 'blue', logo: localLogo('china-construction-third-engineering-bureau.png', 'https://3bur.cscec.com/') },
  { name: '其他已服务银行客户', shortName: '更多客户', tone: 'blue' },
]

export const companyStats = [
  { value: '2006', unit: '年', label: '公司成立', icon: '/images/company-stats/founded.png', note: '待核实' },
  { value: '10000', unit: '㎡', label: '生产基地', icon: '/images/company-stats/factory.png', note: '待核实' },
  { value: '30+', unit: '地区', label: '服务区域', icon: '/images/company-stats/regions.png', note: '待核实' },
  { value: '3000+', unit: '个', label: '项目经验', icon: '/images/company-stats/projects.png', note: '待核实' },
]

const bankExterior = 'https://img.caixin.com/2023-08-25/169296732521192.jpg'
const citicExterior = bankExterior
const citicLobby = 'https://img12.iqilu.com/10339/sucaiku/compress/202502/26/0f3f76ec56e1425890c03d57a8276f59.png'
const bocInterior = 'https://img2.chinadaily.com.cn/images/202005/06/5eb21a49a310a8b2fa435b67.jpeg'
const bocLobby = 'https://p5.itc.cn/images01/20220118/13942fc590ff42b4bbb1d284d8dd56dc.jpeg'
const citicInterior = 'https://p0.itc.cn/q_70/images03/20221230/1aac65092bed4e0abb649d764786ef96.jpeg'
const bocExteriorAlt = 'https://stock.gucheng.com/UploadFiles_7844/202406/2024060211153025.jpg'
const icbcExterior = 'https://static.suzhouhui.com/yh_pic/gongshang/bank/8.jpg'
const icbcAtm = '/assets/bank-atm.jpg'

const solutionImage = (src: string, sourceId: string, alt: string): ImageAsset => ({ src, sourceId, alt })

const solutionExteriorDesktop = '/images/solutions/solution-exterior-desktop.jpg'
const solutionExteriorMobile = '/images/solutions/solution-exterior-mobile.jpg'
const solutionInteriorDesktop = '/images/solutions/solution-interior-desktop.jpg'
const solutionInteriorMobile = '/images/solutions/solution-interior-mobile.jpg'
const solutionSoftspaceDesktop = '/images/solutions/solution-softspace-desktop.jpg'
const solutionSoftspaceMobile = '/images/solutions/solution-softspace-mobile.jpg'
const solutionAtmDesktop = '/images/solutions/solution-atm-desktop.jpg'
const solutionAtmMobile = '/images/solutions/solution-atm-mobile.jpg'
const solutionUpgradeDesktop = '/images/solutions/solution-upgrade-desktop.jpg'
const solutionUpgradeMobile = '/images/solutions/solution-upgrade-mobile.jpg'

export const heroImage: ImageAsset = {
  src: '/images/hero-finance.jpg',
  mobileSrc: '/images/hero-mobile-transparent.png',
  alt: '昊野智能大楼实景图，包含昊野智能横向招牌与广东昊野智能技术有限公司竖式招牌',
  sourceId: 'haoye-hero-building-final',
}

export const solutions: Array<{ title: string; description: string; image: ImageAsset; mobileImage: ImageAsset }> = [
  {
    title: '银行网点外立面形象',
    description: '从门楣、标识到夜间识别，统一网点对外品牌形象。',
    image: solutionImage(solutionExteriorDesktop, 'solution-exterior-desktop', '中国农业银行大楼外立面实景图'),
    mobileImage: solutionImage(solutionExteriorMobile, 'solution-exterior-mobile', '中国农业银行大楼外立面实景图'),
  },
  {
    title: '营业厅室内VI标识',
    description: '梳理空间层级与服务触点，让品牌规范真正落到现场。',
    image: solutionImage(solutionInteriorDesktop, 'solution-interior-desktop', '中信银行营业厅室内VI实景图'),
    mobileImage: solutionImage(solutionInteriorMobile, 'solution-interior-mobile', '中信银行营业厅室内VI实景图'),
  },
  {
    title: '特色网点软装落地',
    description: '结合网点主题、软装陈设与安装落地，打造更具识别度的特色服务空间。',
    image: solutionImage(solutionSoftspaceDesktop, 'solution-softspace-desktop', '招商银行特色网点软装实景图'),
    mobileImage: solutionImage(solutionSoftspaceMobile, 'solution-softspace-mobile', '招商银行特色网点软装实景图'),
  },
  {
    title: '智能设备与ATM防护',
    description: '围绕设备安全、隐私与维护效率，提供适配现场的防护方案。',
    image: solutionImage(solutionAtmDesktop, 'solution-atm-desktop', '中国工商银行ATM与智能设备实景图'),
    mobileImage: solutionImage(solutionAtmMobile, 'solution-atm-mobile', '中国工商银行ATM与智能设备实景图'),
  },
  {
    title: '网点批量升级改造',
    description: '以标准化、模块化方式管理多网点项目，提升交付稳定性。',
    image: solutionImage(solutionUpgradeDesktop, 'solution-upgrade-desktop', '中国工商银行蓝色系新网点实景图'),
    mobileImage: solutionImage(solutionUpgradeMobile, 'solution-upgrade-mobile', '中国工商银行蓝色系新网点实景图'),
  },
]

export const advantages = [
  { title: '专业专注', description: '深耕金融VI标识，理解银行网点建设需求', icon: '/assets/icons/why-haoye/professional.png.png' },
  { title: '一体化能力', description: '从设计深化到制造施工，提供一体化服务', icon: '/assets/icons/why-haoye/integrated.png.png' },
  { title: '智能制造', description: '依托生产制造体系，保障品质与稳定交付', icon: '/assets/icons/why-haoye/manufacturing.png.png' },
  { title: '长期服务', description: '支持银行网点长期维护、升级与持续优化', icon: '/assets/icons/why-haoye/service.png.png' },
]

export type Project = {
  slug: string
  bank: string
  title: string
  location: string
  scope: string
  category: string
  image: ImageAsset
  year?: string
  overview?: string
  gallery?: ImageAsset[]
  serviceCapabilities?: string[]
}

export const projects: Project[] = [
  { slug: 'citic-exterior-reference', bank: '中信银行', title: '营业网点外立面形象参考', location: '广东', scope: '门楣 / 外立面 / 24小时区', category: '股份制银行', image: { src: bankExterior, alt: '中信银行营业网点外立面真实公开照片', sourceId: 'citic-bank-exterior' } },
  { slug: 'icbc-branch-front-reference', bank: '中国工商银行', title: '营业网点门头形象参考', location: '广东', scope: '门楣 / 标识 / 外立面', category: '国有银行', image: { src: icbcExterior, alt: '工商银行营业网点外立面真实公开照片', sourceId: 'icbc-branch-exterior' } },
  { slug: 'abc-interior-wayfinding-reference', bank: '中国农业银行', title: '营业厅空间与导视参考', location: '广东·佛山', scope: '室内VI / 导视 / 服务区', category: '国有银行', image: { src: bocLobby, alt: '银行营业厅内部服务区真实公开照片', sourceId: 'bank-of-china-lobby' } },
  { slug: 'bocom-interior-service-reference', bank: '交通银行', title: '网点室内服务空间参考', location: '广东·广州', scope: '室内VI / 导视 / 营销展示', category: '股份制银行', image: { src: citicInterior, alt: '银行营业厅室内服务空间真实公开照片', sourceId: 'citic-bank-interior' } },
  { slug: 'ccb-brand-space-reference', bank: '中国建设银行', title: '营业厅品牌空间参考', location: '广东', scope: '室内VI / 服务区 / 标识', category: '国有银行', image: { src: bocLobby, alt: '银行营业厅大厅真实公开照片', sourceId: 'bank-of-china-lobby' } },
  { slug: 'pingan-atm-service-reference', bank: '平安银行', title: '24小时自助服务区参考', location: '广东', scope: '自助区 / ATM防护 / 标识', category: '股份制银行', image: { src: icbcAtm, alt: '银行24小时自助服务区域真实公开照片', sourceId: 'bank-self-service-foshan' } },
  { slug: 'icbc-exterior-identity-reference', bank: '中国工商银行', title: '网点外立面形象参考', location: '广东', scope: '外立面 / 门楣 / 夜间识别', category: '国有银行', image: { src: icbcExterior, alt: '工商银行营业网点外立面真实公开照片', sourceId: 'icbc-branch-exterior' } },
  { slug: 'boc-exterior-maintenance-reference', bank: '中国银行', title: '营业网点外立面形象参考', location: '广东·广州', scope: '标识 / 导视 / 维护', category: '国有银行', image: { src: bocExteriorAlt, alt: '中国银行营业网点外立面真实公开照片', sourceId: 'bank-of-china-guangzhou-exterior' } },
]

export const processSteps = [
  { id: '01', title: '需求调研', description: '了解网点现状、品牌规范与实际建设需求', icon: '/assets/icons/process/process-01-research.png' },
  { id: '02', title: '设计方案', description: '结合现场条件制定整体实施与视觉方案', icon: '/assets/icons/process/process-02-solution.png' },
  { id: '03', title: '设计深化', description: '完成尺寸、材料、结构及VI细节深化', icon: '/assets/icons/process/process-03-design.png' },
  { id: '04', title: '智能制造', description: '依托自有生产体系完成标准化生产制造', icon: '/assets/icons/process/process-04-manufacturing.png' },
  { id: '05', title: '工程实施', description: '统筹现场施工、进度及各专业协调', icon: '/assets/icons/process/process-05-engineering.png' },
  { id: '06', title: '安装验收', description: '完成安装调试、质量检查及交付验收', icon: '/assets/icons/process/process-06-acceptance.png' },
  { id: '07', title: '售后服务', description: '提供长期维护、升级及持续服务支持', icon: '/assets/icons/process/process-07-service.png' },
]

export const manufacturingStats = [
  { value: '10000+', unit: '㎡', label: '生产与装配空间', note: '待核实' },
  { value: '120+', unit: '项', label: '工艺与设备协同', note: '待核实' },
  { value: '80+', unit: '人', label: '制造与交付团队', note: '待核实' },
  { value: '30%', unit: '+', label: '批量交付效率目标', note: '待核实' },
]

export const manufacturingImages: ImageAsset[] = [
  { src: '/assets/manufacturing-floor.png', alt: '标识制造生产车间与数控设备临时视觉占位', sourceId: 'generated-manufacturing-floor' },
  { src: '/assets/manufacturing-sheet-metal.png', alt: '标识制造钣金加工设备临时视觉占位', sourceId: 'generated-manufacturing-sheet-metal' },
  { src: '/assets/manufacturing-welding.png', alt: '标识制造焊接工序临时视觉占位', sourceId: 'generated-manufacturing-welding' },
  { src: '/assets/manufacturing-lightbox.png', alt: '标识灯箱制作与装配临时视觉占位', sourceId: 'generated-manufacturing-lightbox' },
  { src: '/assets/manufacturing-quality.png', alt: '标识制造品质检测临时视觉占位', sourceId: 'generated-manufacturing-quality' },
  { src: '/assets/manufacturing-packaging.png', alt: '标识成品包装与仓储物流临时视觉占位', sourceId: 'generated-manufacturing-packaging' },
]

export const qualifications: Array<{ title: string; image: ImageAsset }> = [
  {
    title: 'ISO 9001 质量管理体系',
    image: { src: '/images/qualifications/qualification-iso-9001.png', alt: 'ISO 9001质量管理体系认证证书', sourceId: 'qualification-iso-9001' },
  },
  {
    title: 'ISO 14001 环境管理体系',
    image: { src: '/images/qualifications/qualification-iso-14001.png', alt: 'ISO 14001环境管理体系认证证书', sourceId: 'qualification-iso-14001' },
  },
  {
    title: 'ISO 45001 职业健康安全体系',
    image: { src: '/images/qualifications/qualification-iso-45001.png', alt: 'ISO 45001职业健康安全管理体系认证证书', sourceId: 'qualification-iso-45001' },
  },
  {
    title: '钢结构工程专业承包二级',
    image: { src: '/images/qualifications/qualification-steel-structure.png', alt: '钢结构工程专业承包二级建筑业企业资质证书', sourceId: 'qualification-steel-structure' },
  },
  {
    title: '安全生产许可证',
    image: { src: '/images/qualifications/qualification-safety-production.png', alt: '安全生产许可证', sourceId: 'qualification-safety-production' },
  },
]

export const insights = [
  { date: '行业洞察 · 01', title: '银行网点形象升级的关键要素与趋势' },
  { date: '项目管理 · 02', title: '银行VI标准在批量网点实施中的落地管理' },
  { date: '空间体验 · 03', title: '网点导视系统如何兼顾品牌统一与使用效率' },
]
